/**
 * Implements a component look up utility
 */

import _ from "underscore";
import DOMPurify from "dompurify";
import { rangeFormatter } from "./";

import templates from "../templates/components";
import { rules, types, numberRangeFormats } from "../config/components";

import "../../../sass/components.scss";

const utils = {
  create(c) {
    //console.groupCollapsed(c.elementId)
    //console.log("components::create input", c)

    let html = "",
      options = {};

    // convert old type to new name, or use original if new name not found.
    const type = this.translate(c.element, c.type) || {
      element: c.element,
      type: c.type,
    };

    // look up template by component type
    const template = this.find(type.element, type.type);
    if (template) {
      // merge options
      options = Object.assign({}, template.options, c, type);

      // warn when a dropdown is missing its options
      if (
        (options.type === "single" || options.type === "multiple") &&
        !options.options
      ) {
        console.warn(
          "The dropdown configuration for %s is missing its options.",
          options.requestParameterName
        );
        // set to empty so it renders, but it will be useless
        options.options = [];
      }

      // range inputs and sliders need a range
      const types = ["numeric_range", "range_slider"];
      if (_.contains(types, options.type)) {
        options.range = this.calculateRange(options);
      }

      // extract selected criteria
      options.selectedKeys = [];
      if (options.currentSelection) {
        if (Array.isArray(options.currentSelection)) {
          options.selectedKeys = _.pluck(options.currentSelection, "key") || [];
        } else {
          options.selectedKeys = [options.currentSelection];
        }
      }

      //console.log("components::generate template options", options)

      // create HTML markup
      html = _.template(template.tpl)(options);
    }

    //console.log("components::generate output", html)
    //console.groupEnd()

    return html;
  },

  generate(components) {
    // console.log("components::generate input", components)

    let html = "";
    _.each(components, (config) => {
      if (_.isEmpty(config.elementId)) {
        console.warn(
          "Component cannot be generated because it is missing a name",
          config
        );
      } else {
        const generatedMarkup = this.create(config);

        if (rules.sanitize) {
          const sanitizedMarkup = DOMPurify.sanitize(generatedMarkup);
          if (DOMPurify.removed.length === 0) {
            html += sanitizedMarkup;
          } else {
            // mark input tags disabled
            html += sanitizedMarkup.replace(/input /g, "input disabled ");
            console.warn(
              `Insecure markup found in ${config.description} component.`,
              {
                config,
                generated: generatedMarkup,
                sanitized: sanitizedMarkup,
                removed: DOMPurify.removed,
              }
            );
          }
        } else {
          html += generatedMarkup;
        }
      }
    });

    return html;
  },

  /**
   * Used to determine a user-friendly set of steps for slider values
   *
   * @param {type} totalRange
   * @returns {Object}
   */
  calculateRange(options) {
    // default dummy range. See comments below for details
    let range = {
      min: 1,
      max: 1,
      step: 1,
      initial: [1, 1],
    };

    // keyring
    let keys = [];

    // confirm totalRange is an array
    if (Array.isArray(options.totalRange) && !_.isEmpty(options.totalRange)) {
      // pluck numeric values from totalRange array
      keys = _.pluck(options.totalRange, "key");
      range = {
        initial: [_.min(keys), _.max(keys)],
        // pull min and max values from keys
        min: _.min(keys),
        max: _.max(keys),
      };

      // absolute diff between min and max
      range.diff = Math.abs(range.max - range.min);

      // step calculation for integers
      if ("integer" === options.datatype) {
        range.step = 1;
      } else {
        // floats and currency
        // calculate 2.5% step of diff between max and min values
        range.step = Math.floor(range.diff * 0.025) || 1;
      }

      // override step when range diff is between 0 and 15 for configured number range formats
      if (range.diff > 0 && range.diff <= 15) {
        range.step = _.contains(numberRangeFormats, options.format)
          ? (range.step = 0.25)
          : (range.step = 1);
      }

      // increase step as necessary based on diff
      if (range.diff > 1000000) {
        range.step = 10000;
      } else if (range.diff > 100000) {
        range.step = 1000;
      }

      range.value =
        Array.isArray(options.currentRange) && !_.isEmpty(options.currentRange)
          ? _.pluck(options.currentRange, "key")
          : range.initial;

      // make sure range values are numeric
      try {
        range.value = range.value.map((val) => parseInt(val, 10));
      } catch (err) {
        console.error(err.stack);
      }

      range.label = rangeFormatter(range.value);
    } else if (
      "integer" !== options.datatype &&
      "numeric_range" === options.type
    ) {
      // fixed step for decimal/float/currency types
      range.step = 0.25;
    }

    return range;
  },

  /**
   * Retrieves the requested component template when found, otherwise returns null
   *
   * @param {String} type
   * @param {String} name
   * @returns {component}
   */
  find(type, name) {
    return templates[type] && templates[type][name]
      ? templates[type][name]
      : null;
  },

  /**
   * Converts element types from old JSON API to new ones defined by UI framework.
   */
  translate(element) {
    const type = _.findWhere(types, { element });
    if (_.has(type, "properties")) {
      return type.properties;
    }
    return null;
  },
};

export { utils as default };
